import React, { useRef } from 'react';
import { useDialogLayer } from '../hooks/useDialogLayer';
import { createPortal } from 'react-dom';
import { useContent } from '../contexts/ContentContext';
import GuideHelpButton from './GuideHelpButton';
import { GuideHelpId } from '../utils/guideHelp';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    panelClassName?: string;
    backdropClassName?: string;
    manageFocus?: boolean;
    restoreFocusSelector?: string;
    helpSection?: GuideHelpId;
    headerContent?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, title, panelClassName = '', backdropClassName = '', manageFocus = true, restoreFocusSelector, helpSection, headerContent }) => {
    const { content, language } = useContent();
    const modalContentRef = useRef<HTMLDivElement>(null);

    useDialogLayer(modalContentRef, isOpen && manageFocus, onClose, restoreFocusSelector);

    if (!isOpen) return null;

    return createPortal(
        <div className={`fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${backdropClassName}`} onClick={onClose}>
            <div
                ref={modalContentRef}
                tabIndex={-1}
                role={manageFocus ? 'dialog' : undefined}
                aria-modal={manageFocus || undefined}
                aria-label={manageFocus ? title : undefined}
                data-help-id={helpSection}
                className={`relative bg-white dark:bg-gray-800 ivory:bg-coffee-100 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 opacity-100 ${panelClassName}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="lab-modal-header flex items-center justify-between p-4 border-b dark:border-gray-700 ivory:border-coffee-200 bg-white dark:bg-gray-800 ivory:bg-coffee-100 sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white ivory:text-coffee-800 break-words min-w-0 mr-3">
                        {title || ''}
                    </h2>
                    <div className="flex items-center gap-1 shrink-0">
                    {headerContent}
                    {helpSection && <GuideHelpButton section={helpSection} />}
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ivory:text-coffee-600 ivory:hover:text-coffee-800 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ivory:hover:bg-coffee-200 focus-visible:outline focus-visible:outline-2"
                        aria-label={content.ui[language].common.close}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-0">
                    {children}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
